/**
 * The route table.
 *
 * One definition drives both the sidebar and the router, and every entry
 * carries the permission it requires. In the Base44 build the nav array carried
 * permissions but the router did not, so typing a URL straight into the address
 * bar reached pages the sidebar had hidden — and Purchase Orders, Company
 * Settings and Email Templates were not gated at all.
 */

import { lazy } from 'react';

/**
 * The flag that stops a reload loop: cleared by update-context once any page
 * boots successfully, so each genuine failure gets exactly one retry.
 */
const CHUNK_RELOAD_FLAG = 'stockwise.chunk-reloaded';

/**
 * A lazy route that survives its own chunk going missing.
 *
 * When a deploy replaces the hashed bundles while a tab from the previous
 * build is still open, that tab's next route change can request a file that no
 * longer exists. The failure mode without this was a silent black page; the
 * fix is what the user would do anyway — reload, landing on the new build.
 */
function lazyPage(loader) {
  return lazy(() =>
    loader().catch((error) => {
      let alreadyRetried = false;
      try {
        alreadyRetried = sessionStorage.getItem(CHUNK_RELOAD_FLAG) === '1';
        if (!alreadyRetried) sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1');
      } catch {
        // Storage unavailable: still reload once per page lifetime, since the
        // page this reload produces starts a fresh lifetime.
      }

      if (!alreadyRetried) {
        window.location.reload();
        // Never settles — the page is on its way out.
        return new Promise(() => {});
      }

      throw error;
    }),
  );
}
import {
  ClipboardList,
  LayoutDashboard,
  Mail,
  MinusCircle,
  Package,
  ScanLine,
  Settings as SettingsIcon,
  ShoppingCart,
  Truck,
  Users,
} from 'lucide-react';

const Dashboard = lazyPage(() => import('./pages/Dashboard.jsx'));
const Products = lazyPage(() => import('./pages/Products.jsx'));
const Vendors = lazyPage(() => import('./pages/Vendors.jsx'));
const PurchaseOrders = lazyPage(() => import('./pages/PurchaseOrders.jsx'));
const Receiving = lazyPage(() => import('./pages/Receiving.jsx'));
const Allocations = lazyPage(() => import('./pages/Allocations.jsx'));
const EmailTemplates = lazyPage(() => import('./pages/EmailTemplates.jsx'));
const CompanySettings = lazyPage(() => import('./pages/CompanySettings.jsx'));
const Settings = lazyPage(() => import('./pages/Settings.jsx'));
const UserManagement = lazyPage(() => import('./pages/UserManagement.jsx'));

/**
 * `permission: null` means any signed-in, active user.
 * `nav: false` keeps a page out of the sidebar but still routable.
 */
export const routes = [
  {
    path: '/dashboard',
    title: 'Dashboard',
    icon: LayoutDashboard,
    permission: null,
    Component: Dashboard,
  },
  {
    path: '/products',
    title: 'Products',
    icon: Package,
    permission: 'can_view_products',
    Component: Products,
  },
  {
    path: '/vendors',
    title: 'Vendors',
    icon: Truck,
    permission: 'can_view_vendors',
    Component: Vendors,
  },
  {
    path: '/purchase-orders',
    title: 'Purchase Orders',
    icon: ShoppingCart,
    // Was ungated entirely in the original, despite the flag existing.
    permission: 'can_view_pos',
    Component: PurchaseOrders,
  },
  {
    path: '/receiving',
    title: 'Receiving',
    icon: ScanLine,
    permission: 'can_receive',
    Component: Receiving,
  },
  {
    path: '/allocations',
    title: 'Allocations',
    icon: MinusCircle,
    permission: 'can_allocate',
    Component: Allocations,
  },
  {
    path: '/email-templates',
    title: 'Email Templates',
    icon: Mail,
    permission: 'can_edit_pos',
    Component: EmailTemplates,
  },
  {
    path: '/company-settings',
    title: 'Company Settings',
    icon: ClipboardList,
    permission: 'can_manage_users',
    Component: CompanySettings,
  },
  {
    path: '/users',
    title: 'User Management',
    icon: Users,
    permission: 'can_manage_users',
    Component: UserManagement,
  },
  {
    path: '/settings',
    title: 'My Settings',
    icon: SettingsIcon,
    permission: null,
    // Existed in the original but had no link anywhere, so nobody could reach it.
    Component: Settings,
  },
];

export const HOME_PATH = '/dashboard';

/** Nav entries this profile may see, in sidebar order. */
export function visibleRoutes(can) {
  return routes.filter((route) => route.nav !== false && can(route.permission));
}

/**
 * Where to send someone whose landing page they cannot access — the first page
 * they *can* reach, so an allocator signing in lands on Allocations rather than
 * bouncing off a permission wall.
 */
export function landingPath(can) {
  if (can(null)) {
    const home = routes.find((route) => route.path === HOME_PATH);
    if (home && can(home.permission)) return HOME_PATH;
  }
  const first = visibleRoutes(can)[0];
  return first ? first.path : HOME_PATH;
}
